import { computed, Injectable, signal, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, tap, type Observable } from 'rxjs';
import { type Message } from '@ag-ui/client';
import { KeycloakClient } from '@core/services/keycloak/keycloak.client';
import { environment } from '@environments/environment';
import type { Session } from '../models/chat';

interface SessionsResponse {
  sessions: {
    id: string;
    title: string;
    created_at: string;
    updated_at: string;
  }[];
  next_cursor: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ChatHistoryService {
  private readonly PAGE_SIZE = 10;

  private readonly httpClient = inject(HttpClient);
  private readonly keycloakClient = inject(KeycloakClient);

  private readonly baseUrl = environment.agent.url;

  private readonly _sessions = signal<Session[]>([]);
  private readonly _searchQuery = signal<string>('');
  private readonly _nextCursor = signal<string | null>(null);
  private readonly _isLoading = signal(false);

  readonly sessions = this._sessions.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly hasMore = computed(() => this._nextCursor() !== null);

  readonly filteredSessions = computed(() => {
    const query = this._searchQuery().toLowerCase().trim();
    const sessions = query
      ? this._sessions().filter((s) => s.title.toLowerCase().includes(query))
      : [...this._sessions()];
    return sessions.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  });

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${this.keycloakClient.getAccessToken()}`,
    });
  }

  search(query: string): void {
    this._searchQuery.set(query);
  }

  loadSession(sessionId: string): Observable<Message[]> {
    return this.httpClient.get<Message[]>(
      `${this.baseUrl}/sessions/${sessionId}/messages?after_seq=0`,
      { headers: this.authHeaders() },
    );
  }

  private fetchPage(
    cursor: string | null,
  ): Observable<{ sessions: Session[]; nextCursor: string | null }> {
    let params = new HttpParams().set('limit', this.PAGE_SIZE);
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.httpClient
      .get<SessionsResponse>(`${this.baseUrl}/sessions/`, {
        params,
        headers: this.authHeaders(),
      })
      .pipe(
        map((response) => ({
          sessions: response.sessions.map((session) => ({
            id: session.id,
            title: session.title,
            createdAt: session.created_at,
            updatedAt: session.updated_at,
          })),
          nextCursor: response.next_cursor,
        })),
      );
  }

  loadFirstPage(): void {
    this._isLoading.set(true);
    this.fetchPage(null)
      .pipe(tap(() => this._isLoading.set(false)))
      .subscribe({
        next: (response) => {
          this._sessions.set(response.sessions);
          this._nextCursor.set(response.nextCursor);
        },
        error: () => this._isLoading.set(false),
      });
  }

  loadMore(): void {
    if (this._isLoading() || this._nextCursor() === null) {
      return;
    }
    this._isLoading.set(true);
    this.fetchPage(this._nextCursor())
      .pipe(tap(() => this._isLoading.set(false)))
      .subscribe({
        next: (response) => {
          this._sessions.update((list) => [...list, ...response.sessions]);
          this._nextCursor.set(response.nextCursor);
        },
        error: () => this._isLoading.set(false),
      });
  }

  deleteSession(sessionId: string): Observable<void> {
    return this.httpClient
      .delete<void>(`${this.baseUrl}/sessions/${sessionId}`, {
        headers: this.authHeaders(),
      })
      .pipe(
        tap(() =>
          this._sessions.update((list) =>
            list.filter((s) => s.id !== sessionId),
          ),
        ),
      );
  }
}
